// Structured data (schema.org JSON-LD) built only from facts the site states.
import { AUTHOR, BUSINESS, PHONE, SITE_URL, absoluteUrl } from './site';
import { postDates } from './blogApi';

const BUSINESS_ID = `${SITE_URL}/#business`;

const authorPerson = () => ({
  '@type': 'Person',
  name: AUTHOR.name,
  jobTitle: AUTHOR.jobTitle,
  url: absoluteUrl(AUTHOR.url),
  worksFor: { '@id': BUSINESS_ID },
});

export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HousePainter',
    '@id': BUSINESS_ID,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    url: `${SITE_URL}/`,
    email: BUSINESS.email,
    ...(PHONE && { telephone: PHONE }),
    logo: absoluteUrl(BUSINESS.logo),
    image: absoluteUrl(BUSINESS.image),
    address: { '@type': 'PostalAddress', ...BUSINESS.address },
    areaServed: BUSINESS.areaServed.map((name) => ({ '@type': 'Place', name })),
    founder: { '@type': 'Person', name: AUTHOR.name, jobTitle: AUTHOR.jobTitle, url: absoluteUrl(AUTHOR.url) },
    sameAs: BUSINESS.sameAs,
  };
}

export function faqPageSchema(items, path = '/faq') {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url: `${SITE_URL}${path}`,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

// The post's author: the owner unless the post names someone else.
export function postAuthor(post) {
  const named = (post.author || '').trim();
  if (!named || named === BUSINESS.name || named === AUTHOR.name) return null;
  return named;
}

export function blogPostingSchema(post) {
  const url = absoluteUrl(`/blog/${post.slug}`);
  const { published, modified } = postDates(post);
  const other = postAuthor(post);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: url,
    url,
    headline: post.title,
    description: post.seo?.meta_description || post.excerpt,
    image: post.cover_image_url ? absoluteUrl(post.cover_image_url) : undefined,
    articleSection: post.category || undefined,
    datePublished: published,
    dateModified: modified,
    author: other ? { '@type': 'Person', name: other } : authorPerson(),
    publisher: {
      '@type': 'Organization',
      '@id': BUSINESS_ID,
      name: BUSINESS.name,
      url: `${SITE_URL}/`,
      logo: { '@type': 'ImageObject', url: absoluteUrl(BUSINESS.logo) },
    },
  };
}

export function blogListSchema(posts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    url: `${SITE_URL}/blog`,
    name: `${BUSINESS.name} blog`,
    publisher: { '@id': BUSINESS_ID },
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: absoluteUrl(`/blog/${post.slug}`),
      datePublished: postDates(post).published,
    })),
  };
}

// The services listed on the site, each linked to its section of /services.
export function servicesSchema(services) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: `${SITE_URL}/services`,
    itemListElement: services.map((service, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: service.name,
        description: service.shortDescription,
        url: `${SITE_URL}/services#${service.slug}`,
        provider: { '@id': BUSINESS_ID },
        areaServed: { '@type': 'AdministrativeArea', name: 'Kent' },
      },
    })),
  };
}

// A town page: painting and decorating offered by the business in one town.
export function townServiceSchema(town) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Painting and decorating in ${town.name}`,
    serviceType: 'Painting and decorating',
    url: `${SITE_URL}${town.path}`,
    provider: { '@id': BUSINESS_ID },
    areaServed: { '@type': 'City', name: town.name },
  };
}

export function aboutPageSchema() {
  const business = localBusinessSchema();
  delete business['@context'];
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    url: `${SITE_URL}/about`,
    about: business,
  };
}
